/**
 * Thanksgiving door signs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const thanksgivingDoorSignsPack: ToilePack = {
    slug: 'thanksgiving-door-signs',
    category: 'season',
    name: 'Thanksgiving door signs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'welcome', label: 'Welcome', subject: 'A vertical door sign lettered "WELCOME" with a wreath of leaves below, a thick border'},
        {key: 'gather', label: 'Gather', subject: 'A vertical door sign lettered "GATHER" in bold with a wheat sprig, a thick border'},
        {key: 'thanks', label: 'Give thanks', subject: 'A vertical door sign lettered "GIVE THANKS" with a pumpkin and leaves, a thick border'},
    ],
    styles: [
        {key: 'rustic', label: 'Rustic kraft', style: 'rustic printable on kraft paper, leaf and wheat borders, warm serif lettering, print-ready', palette: 'Kraft brown, rust, mustard and cream', framing: 'Laid out on a kraft page with an even margin'},
        {key: 'elegant', label: 'Elegant', style: 'elegant printable, thin gold line motifs, refined serif lettering, print-ready', palette: 'Cream, gold and a deep burgundy', framing: 'Laid out on a cream page with wide margins'},
        {key: 'kids', label: 'Kids', style: "kids' thanksgiving printable, cartoon turkeys and leaves, rounded lettering, print-ready", palette: 'Bright orange, yellow, red and brown', framing: 'Laid out on a vertical page with a playful border and a white margin'},
    ],
    directions: ['in cursive lettering', 'as a wreath hanger', 'with a family name blank'],
    directionHint: 'try: with a family name blank',
    seo: {
        title: 'Thanksgiving door signs: welcome, gather and thanks',
        metaDescription: "Nine Thanksgiving door signs — welcome, gather here, give thanks — lettered and bordered, rustic, elegant or kids' style, print-ready.",
        h1: 'Thanksgiving door sign: welcome, gather here and give thanks in three styles',
        tagline: 'Nine signs, three messages, three styles each — from the front door to the dining room.',
        intro: "Three signs for a Thanksgiving gathering: a welcome for guests at the door, a gather-here for the dining room, a give-thanks for the mantel or table. Each is lettered and bordered, drawn rustic burlap-and-kraft, elegant gold-and-cream, or bright kids' style. Tape it up or prop it on a stand. Type your own wording and the set redraws.",
        useCases: [
            'a front door welcome for arriving guests',
            'a dining room or mantel decoration',
            'a family name or house sign for the gathering',
            'a Thanksgiving photo backdrop element',
        ],
        faq: [
            {q: 'What size do these print at?', a: 'Each sign is drawn at portrait A4 proportions with a border, ready to tape up or prop against a stand or easel.'},
            {q: 'Can I change the wording?', a: 'Yes — type your own message, like a family name or a specific year, and the sign redraws with the same border and style.'},
            {q: 'Is there a formal version for a dinner party?', a: "The elegant set is built for that — gold accents and a refined serif — while the rustic set leans casual and the kids' set stays bright."},
            {q: 'Can I laminate it for reuse next year?', a: 'Yes — the flat print-ready design laminates cleanly and stores flat between years.'},
        ],
    },
}
